/*
 |------------------------------------------------------------------------------
 | Event manager utility
 |------------------------------------------------------------------------------
 */
/* ========================================================================== *\
	Imports
/* ========================================================================== */

import { isString, isFunction, forEach, isArray, isNil, merge, isElement } from 'lodash';

import { performQueryAll } from '../utilities/dom';

import './polyfills/CustomEvent.js';
import './polyfills/eventListener';

/* ========================================================================== *\
	Constants
/* ========================================================================== */

const 
	blankElement = document.createElement('div'),
    
	originalPreventDefault = '__way__originalPreventDefault',
    
	needsPatching = {
		preventDefault: false
	},
    
	registrationMethod = {
		add: 'addEventListener',
		remove: 'removeEventListener'
	};

/* ========================================================================== *\
	Publish & subscribe
/* ========================================================================== */

/**
 * Dispatch an event
 *
 * Gives the ability to dispatch an event with a dataset,
 * so mechanisms listening to it can behave more specifically.
 *
 * @param [eventName]
 * @param [detail]
 */
function publish(eventName, detail) {
    blankElement.dispatchEvent(new CustomEvent(eventName, { detail }));
}

/**
 * Subscribe to an event.
 *
 * Will provide the dataset to the callback function,
 * so we can store our logic to respond to the event at that point.
 *
 * @param eventNames
 * @param callback
 */
function subscribe(eventNames, callback) {
    if (!isFunction(callback)) {
        return;
    }

    if (isString(eventNames)) {
        blankElement.addEventListener(eventNames, callback);

        return eventNames;
    }

    if (isArray(eventNames)) {
        forEach(eventNames, event => subscribe(event, callback));
    }
}

/**
 * Subscribe to an event a single time and then forget about it
 *
 * @param {String} eventName
 * @param {Function} callback
 */
function subscribeOnce(eventName, callback) {
    bindOnce(blankElement, eventName, callback);
}

/**
 * Unsubscribe to an event.
 *
 * @param {String} [eventName]
 * @param {Function} [callback]
 */
function unsubscribe(eventName, callback) {
    if (!isString(eventName) || !isFunction(callback)) {
        return;
    }

    blankElement.removeEventListener(eventName, callback);
}

function bindOnce(element, eventName, callback) {
    const once = event => {
        callback(event);

        element.removeEventListener(eventName, once);
    };

    element.addEventListener(eventName, once);
}

/**
 * Dispatches a custom event from the specified element.
 *
 * @param {HTMLElement} element The element to dispatch the event from.
 * @param {string} eventName The name of the event, this cannot be empty.
 * @param {Object} [details=null] This parameter can be used to pass custom data
 *        along with the event. It will override whatever detail might be set
 *        in the configuration object.
 * @param {EventConfig} [configuration={}] The configuration for the event, it
 *        can be used to alter the bubbling or cancelable nature of the event.
 *        Configuration options which are left out will fallback to the default
 *        configuration. Any details passed along in this object will be
 *        overriden when the detail param has a value other than null/undefined.
 *
 * @returns {null|CustomEvent} The method will return the instance of
 *          CustomEvent which was dispatched. When not all of the required
 *          parameters were supplied, the method will return null.
 */
function dispatchCustomEvent(element, eventName, detail = null, configuration = {}) {
	// We need an element and event name in order to be able to continue.
	if ( element == null || (eventName == null || eventName === '')) {
		return null;
	}

	if (isNil(configuration)) {
		configuration = {};
	}

	// When a detail object has been provided, add it to the configuration.
	if (!isNil(detail)) {
		// Use merge to ensure there is a configuration object to place the
		// details into.
		configuration = merge(
			configuration,
			{ detail }
		)
	}

	// Create the custom event which we will dispatch.
	const customEvent = createCustomEvent(eventName, configuration);
	element.dispatchEvent(customEvent);

	// Return the custom event.
	return customEvent;
}

/**
 * Creates a custom event for the specified name and configuration. If needed
 * its prevent default mechanism will be altered so it works for custom events.
 *
 * @param {string} eventName The name of the event
 * @param {EventConfig} configuration The configuration for the event.
 *
 * @returns {CustomEvent}
 */
function createCustomEvent(eventName, configuration) {
	const customEvent = new CustomEvent(eventName, configuration);

	// Check if the event can be cancelled and if we have to patch the
	// prevent default mechanism.
	if (configuration.cancelable && needsPatching.preventDefault) {
		overridePreventDefault(customEvent);
	}

	return customEvent;
}

/**
 * There is an issue with IE10/11 where it resets the defaultPrevented property
 * to false, even if the preventDefault method has been called in an event
 * handler. To fix this behaviour we will override the preventDefault method.
 * Our implementation will create a custom property to override the default
 * defaultPrevented property. In our implementation we will always return true.
 * Because the custom property only is defined when the preventDefault method is
 * called it still reports false when the default behaviour hasn't
 * been prevented.
 *
 * @param {CustomEvent} customEvent The event to patch.
 *
 * @see {@link https://stackoverflow.com/questions/23349191/event-preventdefault-is-not-working-in-ie-11-for-custom-events}
 */
function overridePreventDefault(customEvent) {
	// Store the original preventDefault method under a different name on
	// the event itself.
	customEvent[originalPreventDefault] = customEvent.preventDefault;

	// Override the preventDefault method of the event.
	customEvent.preventDefault = function preventDefaultExt () {
		this[originalPreventDefault]();

		Object.defineProperty(this, 'defaultPrevented', {
			get: () => true
		});
	}
}

/**
 * Adds an event listener on an element.
 *
 * @param {HTMLElement} element The element to add the listener to.
 * @param {string} eventName The name of the event to listen for.
 * @param {Function} callback The method to be called when the event occurs.
 * @param {boolean=false} capture Configuration for the event listener.
 *
 * @returns {boolean} The method returns true when the listener was added;
 *          otherwise the method returns false.
 */
function addListener(element, eventName, callback, capture = false) {
	return eventRegistration(registrationMethod.add, element, eventName, callback, capture);
}

/**
 *
 *
 * @param {string} query
 * @param {HTMLElement} eventName
 * @param {Function} callback
 * @param {HTMLElement} [scope=document]
 */
function addListenerByQuery(query, eventName, callback, scope = document.body) {
	if (
		!isString(query) ||
		!isString(eventName) ||
		!isFunction(callback) ||
		!isElement(scope)
	) {
		return false;
	}

	const
		elements = performQueryAll(query, scope);

	forEach(elements, element => element.addEventListener(eventName, callback));
}

/**
 * Adds or removes an event listener from an HTML element.
 *
 * @param {registrationMethod} method Indicates whether the event listener
 *        should be added or removed.
 * @param {HTMLElement} element The element to add or remove a listener to/from.
 * @param {string} eventName The name of the event to add or remove.
 * @param {Function} callback The listener to add or remove.
 * @param {boolean=false} capture The configuration for the listener.
 *
 * @returns {boolean} The method returns false when not all required parameters
 *          are provided; otherwise it returns true to indicate the change was
 *          made successfully.
 */
function eventRegistration(method, element, eventName, callback, capture = false) {
	if (
		(!isElement(element) && element !== document)||
		!isString(eventName) ||
		!isFunction(callback)
	) {
		return false;
	}

	element[method](eventName, callback, capture);

	return true;
}

/**
 * Removes an event listener on an element.
 *
 * @param {HTMLElement} element The element to remove the listener from.
 * @param {string} eventName The name of the event to remove the listener for.
 * @param {Function} callback The method to be removed.
 * @param {boolean=false} capture Configuration which was used when the listener
 *        was added.
 *
 * @returns {boolean} The method returns true when the listener was removed;
 *          otherwise the method returns false.
 */
function removeListener(element, eventName, callback, capture = false) {
	return eventRegistration(registrationMethod.remove, element, eventName, callback, capture);
}


/* ========================================================================== *\
	Exports
/* ========================================================================== */

export { bindOnce, publish, subscribe, subscribeOnce, unsubscribe, dispatchCustomEvent, addListenerByQuery, addListener, removeListener };

// Expose this pub sub system to the legacy framework
(window).eventUtils = { publish, subscribe };
